import Container from 'react-bootstrap/Container';
import Image from 'react-bootstrap/Image';
import { Link, useParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Seo from '../components/Seo';
import { getPost } from '../blog/posts';

import '../style/blog.css';

const SITE_URL = 'https://www.kalia-tech.com';

// Renders "text with [label](/path) links" as react-router links; external
// URLs are left as plain text since posts only link internally.
function renderInline(text) {
    const parts = text.split(/\[([^\]]+)\]\((\/[^)]+)\)/g);
    const nodes = [];
    for (let i = 0; i < parts.length; i += 3) {
        if (parts[i]) nodes.push(parts[i]);
        if (parts[i + 1] && parts[i + 2]) {
            nodes.push(<Link to={parts[i + 2]} key={i}>{parts[i + 1]}</Link>);
        }
    }
    return nodes;
}

function renderBlock(block, index) {
    switch (block.type) {
        case 'h2':
            return <h2 key={index}>{block.text}</h2>;
        case 'h3':
            return <h3 key={index}>{block.text}</h3>;
        case 'ul':
            return (
                <ul key={index}>
                    {block.items.map((item, i) => <li key={i}>{renderInline(item)}</li>)}
                </ul>
            );
        case 'p':
        default:
            return <p key={index}>{renderInline(block.text)}</p>;
    }
}

function formatDate(iso, lang) {
    return new Date(iso).toLocaleDateString(lang === 'en' ? 'en-US' : 'fr-FR', {
        year: 'numeric', month: 'long', day: 'numeric'
    });
}

function BlogPostPage() {
    const { slug } = useParams();
    const { t, i18n } = useTranslation();
    const lang = i18n.language?.startsWith('en') ? 'en' : 'fr';

    const post = getPost(slug);
    if (!post) {
        return <Navigate to='/blog' replace />;
    }

    const content = post[lang];
    const canonical = `${SITE_URL}/blog/${post.slug}`;

    const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: content.title,
        description: content.description,
        datePublished: post.date,
        inLanguage: lang,
        mainEntityOfPage: canonical,
        author: { '@type': 'Organization', name: 'Kalia', url: SITE_URL },
        publisher: { '@type': 'Organization', name: 'Kalia', url: SITE_URL, logo: { '@type': 'ImageObject', url: `${SITE_URL}/kalia-favicon.png` } }
    };

    return (
        <div className="blog-post-page">
            <Seo path={`/blog/${post.slug}`} title={content.title} description={content.description} type='article' jsonLd={jsonLd} />
            <main>
                <article className="blog-post">
                    <Container>
                        <div className="blog-post-header">
                            <Link to='/blog' className="blog-back-link">{t('blog.backToBlog')}</Link>
                            <h1>{content.title}</h1>
                            <div className="blog-post-meta">
                                <span>{t('blog.publishedOn')} {formatDate(post.date, lang)}</span>
                                <span> · {post.readingMinutes} {t('blog.minRead')}</span>
                            </div>
                        </div>
                        <Image src={post.image} alt={content.title} fluid className="blog-post-cover" />
                        <div className="blog-post-content">
                            {content.blocks.map(renderBlock)}
                        </div>
                        <div className="blog-post-cta">
                            <h2>{t('blog.cta.title')}</h2>
                            <p>{t('blog.cta.text')}</p>
                            <Link to='/contact' className="btn blog-cta-button">{t('blog.cta.button')}</Link>
                        </div>
                    </Container>
                </article>
            </main>
        </div>
    );
}

export default BlogPostPage;
